'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function AutoLoanCalculator() {
  const t = useTranslations("tools.autoLoanCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Screen size detection
  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  // Calculator state
  const [currentTab, setCurrentTab] = useState<'price' | 'payment'>('price');
  const [inputs, setInputs] = useState({
    autoPrice: 50000,
    monthlyPay: 750,
    loanTerm: 60,
    interestRate: 5,
    cashIncentives: 0,
    downPayment: 10000,
    tradeInValue: 0,
    tradeInOwed: 0,
    salesTax: 4,
    otherFees: 2200,
    includeFeesInLoan: true,
  });

  const [results, setResults] = useState<any>(null);
  const [amortizationSchedule, setAmortizationSchedule] = useState<any[]>([]);

  // Responsive style helper
  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  // Colors
  const colors = {
    primary: '#2563eb',
    primaryHover: '#1d4ed8',
    secondary: '#7c3aed',
    accent: '#f59e0b',
    textPrimary: '#1e293b',
    textSecondary: '#64748b',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(37, 99, 235, 0.2)',
    inputBorder: '#e2e8f0',
    inputFocus: '#2563eb',
  };

  // Styles
  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6, maxWidth: '800px', margin: '0 auto' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        marginBottom: '2rem',
        borderRadius: '1rem',
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 2rem', borderRadius: '1.5rem' }
    ),
    toolContainer: getResponsiveStyle(
      { maxWidth: '100%', margin: '0 auto' },
      {},
      { maxWidth: '100%' },
      { maxWidth: '1200px' }
    ),
    calculatorLayout: getResponsiveStyle(
      { display: 'grid', gap: '2rem', marginTop: '2rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr 1fr' }
    ),
    calculatorForm: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    tabButtons: {
      display: 'flex',
      gap: '0.5rem',
      marginBottom: '1.5rem',
      background: '#f1f5f9',
      padding: '0.25rem',
      borderRadius: '0.5rem',
    },
    tabBtn: (isActive: boolean) => ({
      flex: 1,
      padding: '0.75rem 1rem',
      border: 'none',
      background: isActive ? 'white' : 'transparent',
      color: isActive ? colors.primary : colors.textSecondary,
      fontWeight: 600,
      cursor: 'pointer',
      borderRadius: '0.375rem',
      transition: 'all 0.3s ease',
      boxShadow: isActive ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none',
    }),
    formGroup: {
      marginBottom: '1.5rem',
    },
    label: getResponsiveStyle(
      { display: 'block', marginBottom: '0.5rem', color: '#334155', fontWeight: 600 },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' },
      { fontSize: '1rem' }
    ),
    inputWrapper: {
      position: 'relative' as const,
    },
    inputPrefix: {
      position: 'absolute' as const,
      left: '1rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: colors.textSecondary,
      fontWeight: 500,
    },
    inputSuffix: {
      position: 'absolute' as const,
      right: '1rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: colors.textSecondary,
      fontWeight: 500,
    },
    input: getResponsiveStyle(
      {
        width: '100%',
        padding: '0.75rem 1rem',
        border: `2px solid ${colors.inputBorder}`,
        borderRadius: '0.5rem',
        fontSize: '1rem',
        transition: 'all 0.3s ease',
        boxSizing: 'border-box' as const,
      },
      {},
      {},
      {}
    ),
    inputWithPrefix: {
      paddingLeft: '2rem',
    },
    inputWithSuffix: {
      paddingRight: '3rem',
    },
    checkboxGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      marginTop: '1rem',
    },
    checkbox: {
      width: '1.25rem',
      height: '1.25rem',
      cursor: 'pointer',
    },
    checkboxLabel: {
      margin: 0,
      cursor: 'pointer',
      fontWeight: 500,
      fontSize: '0.95rem',
    },
    calculateButton: getResponsiveStyle(
      {
        width: '100%',
        padding: '1rem',
        background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
        color: 'white',
        border: 'none',
        borderRadius: '0.5rem',
        fontWeight: 700,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        marginTop: '1rem',
      },
      { fontSize: '1rem' },
      { fontSize: '1.1rem' },
      { fontSize: '1.1rem' }
    ),
    results: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    h2: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    h3: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1.5rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.5rem' }
    ),
    monthlyPayment: {
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
      color: 'white',
      padding: '1.5rem',
      borderRadius: '0.75rem',
      textAlign: 'center' as const,
      marginBottom: '1.5rem',
    },
    monthlyPaymentLabel: {
      fontSize: '0.9rem',
      opacity: 0.9,
      marginBottom: '0.5rem',
    },
    monthlyPaymentValue: getResponsiveStyle(
      { fontWeight: 700 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '2.5rem' }
    ),
    resultItem: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '1rem 0',
      borderBottom: `1px solid ${colors.inputBorder}`,
    },
    resultLabel: {
      color: colors.textSecondary,
      fontWeight: 500,
    },
    resultValue: {
      color: colors.textPrimary,
      fontWeight: 600,
    },
    glassCard: getResponsiveStyle(
      {
        background: colors.glassBg,
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: `1px solid ${colors.glassBorder}`,
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.08)',
        marginBottom: '2rem',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.125rem' }
    ),
    list: getResponsiveStyle(
      { lineHeight: 1.8, color: colors.textSecondary, marginLeft: '1.5rem', marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.125rem' }
    ),
    amortizationSection: getResponsiveStyle(
      {
        gridColumn: screenSize === 'desktop' ? '1 / -1' : 'auto',
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        marginTop: '2rem',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    table: {
      width: '100%',
      borderCollapse: 'collapse' as const,
      overflowX: 'auto' as const,
    },
    th: {
      background: '#f1f5f9',
      padding: '0.75rem',
      textAlign: 'left' as const,
      fontWeight: 600,
      color: '#334155',
      borderBottom: `2px solid ${colors.inputBorder}`,
    },
    td: {
      padding: '0.75rem',
      borderBottom: `1px solid ${colors.inputBorder}`,
      color: '#475569',
    },
    tooltip: {
      display: 'inline-block',
      width: '16px',
      height: '16px',
      background: '#94a3b8',
      color: 'white',
      borderRadius: '50%',
      textAlign: 'center' as const,
      lineHeight: '16px',
      fontSize: '12px',
      marginLeft: '0.25rem',
      cursor: 'help',
      position: 'relative' as const,
    },
    faqItem: {
      marginBottom: '1.5rem',
    },
    faqQuestion: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.125rem' }
    ),
    referenceLink: {
      color: colors.primary,
      textDecoration: 'none',
      wordBreak: 'break-word' as const,
    },
  };

  // Format currency
  const formatCurrency = (value: number) => {
    return '$' + value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Calculate loan
  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const {
      autoPrice,
      monthlyPay,
      loanTerm,
      interestRate,
      cashIncentives,
      downPayment,
      tradeInValue,
      tradeInOwed,
      salesTax,
      otherFees,
      includeFeesInLoan,
    } = inputs;

    let loanAmount: number;
    let monthlyPayment: number;
    let totalLoanAmount: number;
    let salesTaxAmount: number;
    let upfrontPayment: number;
    let totalPayments: number;
    let totalInterest: number;
    let totalCost: number;

    const monthlyRate = interestRate / 100 / 12;

    if (currentTab === 'price') {
      const netTradeIn = tradeInValue - tradeInOwed;
      const priceAfterIncentives = autoPrice - cashIncentives;
      salesTaxAmount = priceAfterIncentives * (salesTax / 100);

      if (includeFeesInLoan) {
        loanAmount = priceAfterIncentives - downPayment - netTradeIn + salesTaxAmount + otherFees;
      } else {
        loanAmount = priceAfterIncentives - downPayment - netTradeIn;
      }

      loanAmount = Math.max(0, loanAmount);

      if (monthlyRate === 0) {
        monthlyPayment = loanAmount / loanTerm;
      } else {
        monthlyPayment = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, loanTerm)) / (Math.pow(1 + monthlyRate, loanTerm) - 1);
      }

      totalPayments = monthlyPayment * loanTerm;
      totalInterest = totalPayments - loanAmount;

      upfrontPayment = includeFeesInLoan ?
        downPayment + (tradeInOwed - tradeInValue) :
        downPayment + salesTaxAmount + otherFees + (tradeInOwed - tradeInValue);

      totalCost = totalPayments + Math.max(0, upfrontPayment);
      totalLoanAmount = loanAmount;

    } else {
      if (monthlyRate === 0) {
        loanAmount = monthlyPay * loanTerm;
      } else {
        loanAmount = monthlyPay * (Math.pow(1 + monthlyRate, loanTerm) - 1) / (monthlyRate * Math.pow(1 + monthlyRate, loanTerm));
      }

      monthlyPayment = monthlyPay;
      totalPayments = monthlyPay * loanTerm;
      totalInterest = totalPayments - loanAmount;
      totalLoanAmount = loanAmount;
      salesTaxAmount = 0;
      upfrontPayment = 0;
      totalCost = totalPayments;
    }

    // Generate amortization schedule (first 12 months)
    const schedule = [];
    let balance = loanAmount;

    for (let month = 1; month <= Math.min(loanTerm, 12); month++) {
      const interestPayment = balance * monthlyRate;
      const principalPayment = monthlyPayment - interestPayment;
      balance -= principalPayment;

      schedule.push({
        month,
        payment: monthlyPayment,
        principal: principalPayment,
        interest: interestPayment,
        balance: Math.max(0, balance),
      });
    }

    setResults({
      monthlyPayment,
      totalLoanAmount,
      salesTaxAmount,
      upfrontPayment,
      totalPayments,
      totalInterest,
      totalCost,
      principalPercent: ((loanAmount / (loanAmount + totalInterest)) * 100).toFixed(0),
      interestPercent: ((totalInterest / (loanAmount + totalInterest)) * 100).toFixed(0),
    });

    setAmortizationSchedule(schedule);
  };

  // Input change handler
  const handleInputChange = (field: string, value: any) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  // Initial calculation
  useEffect(() => {
    handleCalculate({ preventDefault: () => {} } as React.FormEvent);
  }, []);

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <h1 style={styles.h1}>{t("title")}</h1>
        <p style={styles.summaryText}>{t("subtitle")}</p>
      </header>

      {/* Calculator Tool */}
      <section style={styles.calculatorTool}>
        <div style={styles.toolContainer}>
          <div style={styles.calculatorLayout}>
            <div style={styles.calculatorForm}>
              <div style={styles.tabButtons}>
                <button
                  type="button"
                  style={styles.tabBtn(currentTab === 'price')}
                  onClick={() => setCurrentTab('price')}
                >
                  {t("tabTotalPrice")}
                </button>
                <button
                  type="button"
                  style={styles.tabBtn(currentTab === 'payment')}
                  onClick={() => setCurrentTab('payment')}
                >
                  {t("tabMonthlyPayment")}
                </button>
              </div>

              <form onSubmit={handleCalculate}>
                {currentTab === 'price' ? (
                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="auto-price">{t("autoPrice")}</label>
                    <div style={styles.inputWrapper}>
                      <span style={styles.inputPrefix}>$</span>
                      <input
                        type="number"
                        id="auto-price"
                        style={{ ...styles.input, ...styles.inputWithPrefix }}
                        value={inputs.autoPrice}
                        onChange={(e) => handleInputChange('autoPrice', parseFloat(e.target.value) || 0)}
                        step="100"
                        required
                      />
                    </div>
                  </div>
                ) : (
                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="monthly-pay">{t("monthlyPayment")}</label>
                    <div style={styles.inputWrapper}>
                      <span style={styles.inputPrefix}>$</span>
                      <input
                        type="number"
                        id="monthly-pay"
                        style={{ ...styles.input, ...styles.inputWithPrefix }}
                        value={inputs.monthlyPay}
                        onChange={(e) => handleInputChange('monthlyPay', parseFloat(e.target.value) || 0)}
                        step="10"
                      />
                    </div>
                  </div>
                )}

                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="loan-term">{t("loanTerm")}</label>
                  <div style={styles.inputWrapper}>
                    <input
                      type="number"
                      id="loan-term"
                      style={{ ...styles.input, ...styles.inputWithSuffix }}
                      value={inputs.loanTerm}
                      onChange={(e) => handleInputChange('loanTerm', parseInt(e.target.value) || 60)}
                      min="12"
                      max="84"
                      required
                    />
                    <span style={styles.inputSuffix}>{t("months")}</span>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="interest-rate">{t("interestRate")}</label>
                  <div style={styles.inputWrapper}>
                    <input
                      type="number"
                      id="interest-rate"
                      style={{ ...styles.input, ...styles.inputWithSuffix }}
                      value={inputs.interestRate}
                      onChange={(e) => handleInputChange('interestRate', parseFloat(e.target.value) || 0)}
                      step="0.1"
                      min="0"
                      max="30"
                      required
                    />
                    <span style={styles.inputSuffix}>%</span>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="cash-incentives">
                    {t("cashIncentives")}
                    <span style={styles.tooltip} title={t("cashIncentivesTooltip")}>?</span>
                  </label>
                  <div style={styles.inputWrapper}>
                    <span style={styles.inputPrefix}>$</span>
                    <input
                      type="number"
                      id="cash-incentives"
                      style={{ ...styles.input, ...styles.inputWithPrefix }}
                      value={inputs.cashIncentives}
                      onChange={(e) => handleInputChange('cashIncentives', parseFloat(e.target.value) || 0)}
                      step="100"
                    />
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="down-payment">
                    {t("downPayment")}
                    <span style={styles.tooltip} title={t("downPaymentTooltip")}>?</span>
                  </label>
                  <div style={styles.inputWrapper}>
                    <span style={styles.inputPrefix}>$</span>
                    <input
                      type="number"
                      id="down-payment"
                      style={{ ...styles.input, ...styles.inputWithPrefix }}
                      value={inputs.downPayment}
                      onChange={(e) => handleInputChange('downPayment', parseFloat(e.target.value) || 0)}
                      step="100"
                    />
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="trade-in-value">
                    {t("tradeInValue")}
                    <span style={styles.tooltip} title={t("tradeInValueTooltip")}>?</span>
                  </label>
                  <div style={styles.inputWrapper}>
                    <span style={styles.inputPrefix}>$</span>
                    <input
                      type="number"
                      id="trade-in-value"
                      style={{ ...styles.input, ...styles.inputWithPrefix }}
                      value={inputs.tradeInValue}
                      onChange={(e) => handleInputChange('tradeInValue', parseFloat(e.target.value) || 0)}
                      step="100"
                    />
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="trade-in-owed">
                    {t("tradeInOwed")}
                    <span style={styles.tooltip} title={t("tradeInOwedTooltip")}>?</span>
                  </label>
                  <div style={styles.inputWrapper}>
                    <span style={styles.inputPrefix}>$</span>
                    <input
                      type="number"
                      id="trade-in-owed"
                      style={{ ...styles.input, ...styles.inputWithPrefix }}
                      value={inputs.tradeInOwed}
                      onChange={(e) => handleInputChange('tradeInOwed', parseFloat(e.target.value) || 0)}
                      step="100"
                    />
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="sales-tax">
                    {t("salesTax")}
                    <span style={styles.tooltip} title={t("salesTaxTooltip")}>?</span>
                  </label>
                  <div style={styles.inputWrapper}>
                    <input
                      type="number"
                      id="sales-tax"
                      style={{ ...styles.input, ...styles.inputWithSuffix }}
                      value={inputs.salesTax}
                      onChange={(e) => handleInputChange('salesTax', parseFloat(e.target.value) || 0)}
                      step="0.1"
                      min="0"
                      max="15"
                    />
                    <span style={styles.inputSuffix}>%</span>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="other-fees">
                    {t("otherFees")}
                    <span style={styles.tooltip} title={t("otherFeesTooltip")}>?</span>
                  </label>
                  <div style={styles.inputWrapper}>
                    <span style={styles.inputPrefix}>$</span>
                    <input
                      type="number"
                      id="other-fees"
                      style={{ ...styles.input, ...styles.inputWithPrefix }}
                      value={inputs.otherFees}
                      onChange={(e) => handleInputChange('otherFees', parseFloat(e.target.value) || 0)}
                      step="50"
                    />
                  </div>
                </div>

                <div style={styles.checkboxGroup}>
                  <input
                    type="checkbox"
                    id="include-fees"
                    style={styles.checkbox}
                    checked={inputs.includeFeesInLoan}
                    onChange={(e) => handleInputChange('includeFeesInLoan', e.target.checked)}
                  />
                  <label style={styles.checkboxLabel} htmlFor="include-fees">
                    {t("includeFeesInLoan")}
                  </label>
                </div>

                <button type="submit" style={styles.calculateButton}>
                  {t("calculateButton")}
                </button>
              </form>
            </div>

            {results && (
              <div style={styles.results}>
                <h3 style={styles.h3}>{t("resultsTitle")}</h3>

                <div style={styles.monthlyPayment}>
                  <div style={styles.monthlyPaymentLabel}>{t("monthlyPaymentLabel")}</div>
                  <div style={styles.monthlyPaymentValue}>{formatCurrency(results.monthlyPayment)}</div>
                </div>

                <div style={styles.resultItem}>
                  <span style={styles.resultLabel}>{t("totalLoanAmount")}</span>
                  <span style={styles.resultValue}>{formatCurrency(results.totalLoanAmount)}</span>
                </div>

                {currentTab === 'price' && (
                  <>
                    <div style={styles.resultItem}>
                      <span style={styles.resultLabel}>{t("salesTaxAmount")}</span>
                      <span style={styles.resultValue}>{formatCurrency(results.salesTaxAmount)}</span>
                    </div>

                    <div style={styles.resultItem}>
                      <span style={styles.resultLabel}>{t("upfrontPayment")}</span>
                      <span style={styles.resultValue}>{formatCurrency(Math.max(0, results.upfrontPayment))}</span>
                    </div>
                  </>
                )}

                <div style={styles.resultItem}>
                  <span style={styles.resultLabel}>{t("totalPayments")}</span>
                  <span style={styles.resultValue}>{formatCurrency(results.totalPayments)}</span>
                </div>

                <div style={styles.resultItem}>
                  <span style={styles.resultLabel}>{t("totalInterest")}</span>
                  <span style={styles.resultValue}>{formatCurrency(results.totalInterest)}</span>
                </div>

                <div style={{ ...styles.resultItem, borderBottom: 'none', fontWeight: 700, fontSize: '1.1rem', color: colors.primary }}>
                  <span style={styles.resultLabel}>{t("totalCost")}</span>
                  <span style={styles.resultValue}>{formatCurrency(results.totalCost)}</span>
                </div>

                <div style={{ marginTop: '2rem', textAlign: 'center' as const }}>
                  <svg width="220" height="180">
                    <text x="110" y="20" textAnchor="middle" fontSize="16" fontWeight="bold" fill={colors.textPrimary}>
                      {t("loanBreakdown")}
                    </text>
                    <path
                      d={`M 110,90 L 110,40 A 50,50 0 ${parseInt(results.principalPercent) > 50 ? 1 : 0},1 ${110 + 50 * Math.cos((parseInt(results.principalPercent) / 100 * 360 - 90) * Math.PI / 180)},${90 + 50 * Math.sin((parseInt(results.principalPercent) / 100 * 360 - 90) * Math.PI / 180)} Z`}
                      fill={colors.primary}
                    />
                    <path
                      d={`M 110,90 L ${110 + 50 * Math.cos((parseInt(results.principalPercent) / 100 * 360 - 90) * Math.PI / 180)},${90 + 50 * Math.sin((parseInt(results.principalPercent) / 100 * 360 - 90) * Math.PI / 180)} A 50,50 0 ${parseInt(results.interestPercent) > 50 ? 1 : 0},1 110,40 Z`}
                      fill={colors.accent}
                    />
                    <circle cx="110" cy="90" r="25" fill="white" />
                    <rect x="30" y="150" width="15" height="15" fill={colors.primary} rx="2" />
                    <text x="50" y="162" fontSize="13" fill="#334155">
                      {t("principal")} {results.principalPercent}%
                    </text>
                    <rect x="30" y="170" width="15" height="15" fill={colors.accent} rx="2" />
                    <text x="50" y="182" fontSize="13" fill="#334155">
                      {t("interest")} {results.interestPercent}%
                    </text>
                  </svg>
                </div>
              </div>
            )}
          </div>

          {amortizationSchedule.length > 0 && (
            <div style={styles.amortizationSection}>
              <h3 style={styles.h3}>{t("amortizationSchedule")}</h3>
              <div style={{ overflowX: 'auto' as const }}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>{t("month")}</th>
                      <th style={styles.th}>{t("payment")}</th>
                      <th style={styles.th}>{t("principal")}</th>
                      <th style={styles.th}>{t("interest")}</th>
                      <th style={styles.th}>{t("balance")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {amortizationSchedule.map((row) => (
                      <tr key={row.month} style={{ background: 'transparent', transition: 'background 0.2s' }}>
                        <td style={styles.td}>{row.month}</td>
                        <td style={styles.td}>{formatCurrency(row.payment)}</td>
                        <td style={styles.td}>{formatCurrency(row.principal)}</td>
                        <td style={styles.td}>{formatCurrency(row.interest)}</td>
                        <td style={styles.td}>{formatCurrency(row.balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("introTitle")}</h2>
        <p style={styles.paragraph}>{t("introParagraph1")}</p>
        <p style={styles.paragraph}>{t("introParagraph2")}</p>
        <p style={styles.paragraph}>{t("introParagraph3")}</p>
      </section>

      {/* How to Use */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("howToUseTitle")}</h2>
        <ol style={styles.list}>
          <li>{t("howToUseStep1")}</li>
          <li>{t("howToUseStep2")}</li>
          <li>{t("howToUseStep3")}</li>
          <li>{t("howToUseStep4")}</li>
          <li>{t("howToUseStep5")}</li>
          <li>{t("howToUseStep6")}</li>
        </ol>
      </section>

      {/* Latest Insights */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("insightsTitle")}</h2>
        <p style={styles.paragraph}>{t("insightsParagraph1")}</p>
        <ul style={styles.list}>
          <li>{t("insightsPoint1")}</li>
          <li>{t("insightsPoint2")}</li>
          <li>{t("insightsPoint3")}</li>
          <li>{t("insightsPoint4")}</li>
        </ul>
        <p style={styles.paragraph}>{t("insightsParagraph2")}</p>
      </section>

      {/* Key Details */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("detailsTitle")}</h2>
        <h3 style={{ ...styles.h3, fontSize: '1.25rem', marginTop: '1.5rem' }}>{t("detailsSubtitle1")}</h3>
        <p style={styles.paragraph}>{t("detailsParagraph1")}</p>

        <h3 style={{ ...styles.h3, fontSize: '1.25rem', marginTop: '1.5rem' }}>{t("detailsSubtitle2")}</h3>
        <ul style={styles.list}>
          <li>{t("detailsPoint1")}</li>
          <li>{t("detailsPoint2")}</li>
          <li>{t("detailsPoint3")}</li>
          <li>{t("detailsPoint4")}</li>
        </ul>

        <h3 style={{ ...styles.h3, fontSize: '1.25rem', marginTop: '1.5rem' }}>{t("detailsSubtitle3")}</h3>
        <p style={styles.paragraph}>{t("detailsParagraph2")}</p>
      </section>

      {/* FAQ */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("faqTitle")}</h2>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faqQuestion1")}</h3>
          <p style={styles.paragraph}>{t("faqAnswer1")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faqQuestion2")}</h3>
          <p style={styles.paragraph}>{t("faqAnswer2")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faqQuestion3")}</h3>
          <p style={styles.paragraph}>{t("faqAnswer3")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faqQuestion4")}</h3>
          <p style={styles.paragraph}>{t("faqAnswer4")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faqQuestion5")}</h3>
          <p style={styles.paragraph}>{t("faqAnswer5")}</p>
        </div>
      </section>

      {/* References */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("referencesTitle")}</h2>
        <ol style={styles.list}>
          <li>
            <a href="https://www.nerdwallet.com/auto-loans/calculators/auto-loan-calculator" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
              {t("reference1")}
            </a>
          </li>
          <li>
            <a href="https://www.bankrate.com/loans/auto-loans/early-payment-payoff-calculator/" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
              {t("reference2")}
            </a>
          </li>
          <li>
            <a href="https://www.lendingtree.com/auto/calculators/payment/" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
              {t("reference3")}
            </a>
          </li>
          <li>
            <a href="https://www.bankofamerica.com/auto-loans/auto-loan-calculator/" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
              {t("reference4")}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}